import { ConfigurationSettings } from 'models/configuration-settings';
import { clients, HttpError } from 'lib/https';
import {
  CreateVoiceSessionRequest,
  VOICE_NOT_CONFIGURED_CODE,
  VoiceSessionResponse,
} from '../types/voice-call.types';

const voiceSessionUrl = (config: ConfigurationSettings, room?: string) => {
  const base = `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.voiceSession}`;
  return room ? `${base}/${encodeURIComponent(room)}` : base;
};

/** Mint a LiveKit room + one-time join ticket. Call on the user's click, not on page load. */
export const createVoiceSession = async (
  config: ConfigurationSettings,
  body: CreateVoiceSessionRequest
): Promise<VoiceSessionResponse> => {
  const payload: CreateVoiceSessionRequest = { agentId: body.agentId };
  if (body.conversationId) payload.conversationId = body.conversationId;
  return clients.post<VoiceSessionResponse>(
    voiceSessionUrl(config),
    config.blocksKey,
    JSON.stringify(payload)
  );
};

/** Server-side hang-up; optional and idempotent (the room also expires on its own). */
export const endVoiceSession = async (
  config: ConfigurationSettings,
  room: string
): Promise<unknown> => clients.delete(voiceSessionUrl(config, room), config.blocksKey);

/**
 * Best-effort hang-up for `pagehide`: `fetch` with `keepalive` can still carry the auth
 * headers, unlike `navigator.sendBeacon`.
 */
export const endVoiceSessionKeepalive = (config: ConfigurationSettings, room: string): void => {
  fetch(voiceSessionUrl(config, room), {
    method: 'DELETE',
    keepalive: true,
    credentials: 'include',
    headers: clients.createHeaders(config.blocksKey, {}),
  }).catch(() => undefined);
};

export type VoiceSessionErrorKind = 'unavailable' | 'failed' | 'unknown';

export interface VoiceSessionErrorInfo {
  kind: VoiceSessionErrorKind;
  message?: string;
}

/** Map a `POST /voice/session` failure onto what the UI should do. */
export const classifyVoiceSessionError = (err: unknown): VoiceSessionErrorInfo => {
  if (!(err instanceof HttpError)) return { kind: 'unknown' };
  const data = err.error?.data;
  const code =
    data && typeof data === 'object' ? (data as Record<string, unknown>).code : undefined;
  if (err.status === 503 || code === VOICE_NOT_CONFIGURED_CODE) return { kind: 'unavailable' };
  if (err.status === 400) {
    const message = typeof err.error?.message === 'string' ? err.error.message : undefined;
    return { kind: 'failed', message };
  }
  return { kind: 'unknown' };
};
